import React from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import type { LandingCopy, LandingPalette } from './landingCopy';

const DEMO_MAIL =
  'mailto:info@skyofsolutions.com?subject=ELOG%20Demo%20Request';

type LandingHeroProps = {
  onSignIn: () => void;
  copy: LandingCopy['hero'];
  palette: LandingPalette;
};

const LandingHero: React.FC<LandingHeroProps> = ({ onSignIn, copy, palette }) => (
  <Box
    id="home"
    component="section"
    sx={{
      position: 'relative',
      minHeight: { xs: '88vh', md: '92vh' },
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'url(/image/hero-aviation.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        background:
          'linear-gradient(105deg, rgba(8,12,18,0.92) 0%, rgba(8,12,18,0.72) 48%, rgba(8,12,18,0.45) 100%)',
      }}
    />

    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 4, md: 5 } }}>
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
      >
        <Typography
          sx={{
            fontFamily: '"Outfit", "Cairo", sans-serif',
            fontWeight: 800,
            fontSize: { xs: '3.2rem', sm: '4.5rem', md: '5.5rem' },
            letterSpacing: '-0.04em',
            lineHeight: 0.95,
            mb: 1.5,
            color: '#fff',
          }}
        >
          ELOG
        </Typography>
        <Typography
          sx={{
            color: '#7DD3FC',
            fontWeight: 600,
            letterSpacing: '0.04em',
            fontSize: { xs: '0.75rem', md: '0.85rem' },
            mb: 3,
          }}
        >
          {copy.brandSub}
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Outfit", "Cairo", sans-serif',
            fontWeight: 700,
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            lineHeight: 1.15,
            maxWidth: 640,
            mb: 2,
            color: '#fff',
          }}
        >
          {copy.headline}
        </Typography>
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.78)',
            fontSize: { xs: '1rem', md: '1.125rem' },
            maxWidth: 560,
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          {copy.body}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            variant="contained"
            size="large"
            onClick={onSignIn}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 3.5,
              py: 1.4,
              borderRadius: 2,
              bgcolor: palette.primaryBtn,
              '&:hover': { bgcolor: palette.primaryBtnHover },
            }}
          >
            {copy.signIn}
          </Button>
          <Button
            component="a"
            href={DEMO_MAIL}
            variant="outlined"
            size="large"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3.5,
              py: 1.4,
              borderRadius: 2,
              borderColor: 'rgba(255,255,255,0.35)',
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.06)',
              '&:hover': { borderColor: '#7DD3FC', bgcolor: 'rgba(125,211,252,0.1)' },
            }}
          >
            {copy.requestDemo}
          </Button>
        </Stack>
      </motion.div>
    </Container>
  </Box>
);

export default LandingHero;
